import type { $TSFixMe } from '@readme/iso';
import type Oas from 'oas';
import type { Operation } from 'oas/operation';

import { HTTP_METHOD } from '@readme/iso';
import React, { useMemo } from 'react';

import { useProjectStore } from '@core/store';

import Method from '@ui/API/Method';
import RDMD from '@ui/RDMD';

import AIDropdown from '../../../routes/SuperHub/Reference/AIDropdown';

import classes from './style.module.scss';
import './style.scss';
import TagTooltip from './TagTooltip';

interface APIHeaderProps {
  className?: string;
  doc: {
    deprecated?: boolean;
    excerpt?: string;
    slug: string;
    title: string;
    type?: string;
  };
  isWebhook?: boolean;
  markdownContent?: string;
  oas?: Oas;
  operation?: Operation;
  servers?: {
    selected?: number;
    variables?: $TSFixMe; // @todo export the `Variables` type in `oas`
  };
}

/**
 * @param {string} host
 * @param {Operation} operation
 */
function createHeaderUrl(host: string, operation: Operation, isWebhook: boolean) {
  const operationId = operation.getOperationId();

  // Split up the string based on the curly brace
  let pathParts: React.ReactNode[] | string[] = operation.path.split(/\{|\}/g);

  if (pathParts.length > 1) {
    const finalPathParts: React.ReactNode[] = [];

    pathParts.forEach((param, index) => {
      // When we split off the curly brace the even elements are the param names
      if (index % 2) {
        finalPathParts.push(
          <label
            key={`path-${operationId}_${param}-${index}`}
            className={classes['APIHeader-url-parameter']}
            htmlFor={`path-${operationId}_${param}`}
          >
            {`{${param}}`}
          </label>,
        );
      } else {
        // And the odd elements are the string literals
        finalPathParts.push(param);
      }
    });

    pathParts = finalPathParts;
  }

  return (
    <span
      className={classes['headline-container-article-info-url']}
      data-testid="serverurl"
      title={`${host}${operation.path}`}
    >
      {!isWebhook && host}
      {pathParts}
    </span>
  );
}

const APIHeader = React.memo(function APIHeader({
  doc,
  isWebhook,
  oas,
  servers,
  operation,
  markdownContent,
}: APIHeaderProps) {
  const opts = useMemo(() => ({ copyButtons: true }), []);

  const [isRmdx, showAIDropdown] = useProjectStore(s => [
    s.data.features.mdx === 'enabled',
    s.data.appearance.ai?.dropdown === 'enabled',
  ]);

  // filter operation tags so we only use tags with at least 2 pieces of metadata
  const tags =
    operation?.getTags().filter(tag => {
      return tag.name && (tag.description || tag.externalDocs);
    }) || [];

  return (
    <>
      <header
        className={`${classes['headline-container']} ${
          doc.type === 'basic' ? classes['headline-container_basic'] : ''
        }`}
        data-raycast-oas={doc.type === 'endpoint' && (oas?.api?._id as string)}
      >
        <div style={{ position: 'absolute', top: '0px', right: '0px', zIndex: 50 }}>
          <AIDropdown doc={doc} markdownContent={markdownContent} operation={operation} />
        </div>
        <div className={`${classes['headline-container-grid-item']} ${classes['button-container']}`}>
          <header id="content-head">
            <h1 className={showAIDropdown ? classes['ai-heading-padding'] : undefined}>
              {doc.title}
              {tags?.length > 0 && <TagTooltip pageId={doc.slug} tags={tags} />}
            </h1>
          </header>
        </div>
        {(doc.type === 'endpoint' || doc.type === 'webhook') && !!oas && !!operation && (
          <div className={classes['headline-container-article-info']}>
            <Method
              className={classes['headline-container-api-method']}
              size="lg"
              type={doc.type === 'webhook' ? HTTP_METHOD.WEBHOOK : (operation.method as HTTP_METHOD)}
            />{' '}
            {!!(operation?.isDeprecated() || doc.deprecated) && (
              <Method className={classes['headline-container-api-method']} size="lg" type={HTTP_METHOD.DEPRECATED} />
            )}
            {createHeaderUrl(oas.url(servers?.selected, {}), operation, !!isWebhook)}
          </div>
        )}
        {!!doc.excerpt && (
          <RDMD
            body={doc.excerpt}
            className={`${classes['grid-item']} ${classes.excerpt}`}
            opts={opts}
            renderer={isRmdx ? 'rmdx' : 'rdmd'}
          />
        )}
      </header>
    </>
  );
});

export default APIHeader;
